/**
 * VideoUploader - Handles video uploads to Mux for automatic HLS transcoding
 *
 * Flow:
 * 1. Frontend requests upload URL from Cloud Function
 * 2. Upload directly to Mux with progress tracking
 * 3. Mux transcodes video to HLS (30s-2min typically)
 * 4. Webhook notifies completion, saves HLS URL to Firestore
 * 5. App plays via StreamingVideoLoader
 *
 * Supports:
 * - Resumable uploads (Mux's direct upload)
 * - Progress tracking
 * - Status polling while transcoding
 */

import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { functions, db } from '../firebase/config';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('VideoUploader');

export type UploadStatus = 'idle' | 'uploading' | 'processing' | 'ready' | 'error';

export interface VideoUploadResult {
  uploadId: string;
  status: UploadStatus;
  hlsUrl?: string;
  thumbnailUrl?: string;
  mp4Url?: string;       // Mux-provided MP4 fallback
  duration?: number;     // Video duration in seconds
  aspectRatio?: string;  // e.g., "16:9"
  error?: string;
}

export interface UploadProgress {
  percent: number;
  bytesUploaded: number;
  totalBytes: number;
  status: UploadStatus;
}

interface MuxUploadUrlResponse {
  uploadUrl: string;
  uploadId: string;
}

/**
 * VideoUploader service for Mux integration
 *
 * @example
 * ```typescript
 * const uploader = new VideoUploader();
 *
 * // Upload with progress tracking
 * const result = await uploader.upload(file, (progress) => {
 *   console.log(`${progress.percent}% uploaded`);
 * });
 *
 * // Wait for transcoding to complete
 * await uploader.waitForReady(result.uploadId, (status) => {
 *   console.log(`Status: ${status}`);
 * });
 * ```
 */
export class VideoUploader {
  private currentUpload: XMLHttpRequest | null = null;
  private statusUnsubscribe: Unsubscribe | null = null;

  /**
   * Upload a video file to Mux
   *
   * @param file - Video file to upload
   * @param onProgress - Progress callback
   * @returns Upload result with uploadId (HLS URL comes later via webhook)
   */
  async upload(
    file: File,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<VideoUploadResult> {
    // Validate file type
    if (!file.type.startsWith('video/')) {
      throw new Error('Invalid file type. Please upload a video file.');
    }

    // Get upload URL from Cloud Function
    const getMuxUploadUrl = httpsCallable<void, MuxUploadUrlResponse>(
      functions,
      'getMuxUploadUrl'
    );

    let uploadUrl: string;
    let uploadId: string;

    try {
      const response = await getMuxUploadUrl();
      uploadUrl = response.data.uploadUrl;
      uploadId = response.data.uploadId;
      logger.debug(`Got Mux upload URL for uploadId: ${uploadId}`);
    } catch (error) {
      logger.error('Failed to get upload URL:', error);
      throw new Error('Failed to initialize upload. Please try again.');
    }

    // Upload directly to Mux
    try {
      await this.uploadWithProgress(uploadUrl, file, (bytesUploaded, totalBytes) => {
        onProgress?.({
          percent: Math.round((bytesUploaded / totalBytes) * 100),
          bytesUploaded,
          totalBytes,
          status: 'uploading',
        });
      });

      logger.info(`Upload complete: ${uploadId}`);

      return {
        uploadId,
        status: 'processing', // Mux is now transcoding
      };
    } catch (error) {
      logger.error('Upload failed:', error);
      throw new Error('Upload failed. Please check your connection and try again.');
    }
  }

  /**
   * Upload file with XMLHttpRequest for progress tracking
   */
  private uploadWithProgress(
    url: string,
    file: File,
    onProgress: (bytesUploaded: number, totalBytes: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      this.currentUpload = xhr;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded, e.total);
        }
      });

      xhr.addEventListener('load', () => {
        this.currentUpload = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        this.currentUpload = null;
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        this.currentUpload = null;
        reject(new Error('Upload cancelled'));
      });

      // Mux expects PUT request with the raw video file
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  /**
   * Cancel current upload
   */
  cancelUpload(): void {
    if (this.currentUpload) {
      this.currentUpload.abort();
      this.currentUpload = null;
      logger.debug('Upload cancelled');
    }
  }

  /**
   * Wait for video processing to complete
   * Listens to Firestore document for status updates from webhook
   *
   * @param uploadId - The upload ID returned from upload()
   * @param onStatusChange - Callback for status updates
   * @param timeout - Maximum wait time in ms (default: 5 minutes)
   * @returns Final upload result with HLS URL
   */
  waitForReady(
    uploadId: string,
    onStatusChange?: (status: UploadStatus) => void,
    timeout: number = 5 * 60 * 1000
  ): Promise<VideoUploadResult> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.stopWatching();
        reject(new Error('Video processing timed out. Please check the dashboard later.'));
      }, timeout);

      // Listen to Firestore document for webhook updates
      const uploadDoc = doc(db, 'videoUploads', uploadId);

      this.statusUnsubscribe = onSnapshot(uploadDoc, (snapshot) => {
        if (!snapshot.exists()) {
          return; // Document not yet created
        }

        const data = snapshot.data();
        const status = data.status as UploadStatus;

        onStatusChange?.(status);

        if (status === 'ready') {
          clearTimeout(timeoutId);
          this.stopWatching();

          resolve({
            uploadId,
            status: 'ready',
            hlsUrl: data.hlsUrl,
            thumbnailUrl: data.thumbnailUrl,
            mp4Url: data.mp4Url,
            duration: data.duration,
            aspectRatio: data.aspectRatio,
          });
        } else if (status === 'error') {
          clearTimeout(timeoutId);
          this.stopWatching();

          reject(new Error(data.error || 'Video processing failed'));
        }
      }, (error) => {
        clearTimeout(timeoutId);
        this.stopWatching();
        reject(error);
      });
    });
  }

  /**
   * Stop watching for status updates
   */
  stopWatching(): void {
    if (this.statusUnsubscribe) {
      this.statusUnsubscribe();
      this.statusUnsubscribe = null;
    }
  }

  /**
   * Get current upload status from Firestore
   */
  async getStatus(uploadId: string): Promise<VideoUploadResult | null> {
    const { getDoc } = await import('firebase/firestore');
    const uploadDoc = doc(db, 'videoUploads', uploadId);
    const snapshot = await getDoc(uploadDoc);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();
    return {
      uploadId,
      status: data.status as UploadStatus,
      hlsUrl: data.hlsUrl,
      thumbnailUrl: data.thumbnailUrl,
      mp4Url: data.mp4Url,
      duration: data.duration,
      aspectRatio: data.aspectRatio,
      error: data.error,
    };
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.cancelUpload();
    this.stopWatching();
  }
}

// Singleton instance for convenience
let videoUploaderInstance: VideoUploader | null = null;

export function getVideoUploader(): VideoUploader {
  if (!videoUploaderInstance) {
    videoUploaderInstance = new VideoUploader();
  }
  return videoUploaderInstance;
}
